// iter 275 round 35 — Container <GalleriaLightbox>.
// iter v28 r358 — User M1267: «rendi la galleria coerente con l'editor: dentro
// la galleria voglio creare video e contenuti come dall'editor». Aggiunti:
//   • Card Prima/Dopo (1080×1350 editoriale, solo per foto live con before+after)
//   • Video Prima/Dopo 8s (MP4, solo per foto live con before+after)
// Tolto il testo "Foto curata con Pronto" dagli share (allineato a r356).
// Layout: 4 bottoni primary sempre visibili + sheet "Più…" con secondary.

import React, { useState } from "react";
import {
  X, DownloadSimple, Share, House, Trash, ArrowsClockwise, Sparkle,
  DotsThreeOutline, FilmStrip, GridFour,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { liveFileUrl, liveBeforeAfterCardUrl, liveBeforeAfterVideoUrl } from "@/lib/fotoUrls";
import { shareVideo, shareImage, downloadImage } from "@/lib/share";
import { BeforeAfterImage } from "@/components/photo/FotoLightboxParts";
import { LiveFeedbackBar } from "@/components/photo/LiveFeedbackBar";

const isLikelyMobile = () =>
  typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

export function GalleriaLightbox({
  item,                    // gallery item (live o property photo)
  view,                    // "before" | "after"
  setView,                 // setter
  onClose,                 // chiudi lightbox
  properties,              // lista properties (per "Usa")
  shareImage,              // ({url, filename, title, text}) → web share
  setImportingId,          // apre picker "Usa in annuncio…"
  deleteOne,               // (realId, source) → cancella
  onRiprova,               // (item) => apre Editor foto in Riprova mode
  onCreaContenuti,         // iter v26 r9 — (item) => apre AdHocSocialModal con la foto
}) {
  const [moreOpen, setMoreOpen] = useState(false);

  if (!item) return null;
  const h = item;
  const realId = h.id || h.photo_id;
  const isLive = h.source === "live" || !h.property_id;
  const liveUuid = (realId || "").replace(/^(live|prop):/, "");
  const afterSrc = h.url || h.enhanced_url || h.image_url || "";
  const beforeSrc = isLive && liveUuid ? liveFileUrl(liveUuid, "before") : "";
  const curSrc = isLive && view === "before" ? beforeSrc : afterSrc;
  // Per video/card serve un live con kind transformative (stage/restyle/enhance)
  // Tutti i live items hanno before+after, quindi è sufficiente isLive + liveUuid.
  const hasBeforeAfter = isLive && !!liveUuid;
  const kindLabel = h.kind === "stage" ? "Arredata"
    : h.kind === "restyle" ? "Rinnovata"
    : h.kind === "furnish_plan" ? "Pianta arredata"
    : h.kind === "enhance" ? "Migliorata"
    : "Foto";
  const filename = `pronto-${(liveUuid || realId || "").slice(0, 8)}.jpg`;
  const beforeFilename = `pronto-originale-${(liveUuid || realId || "").slice(0, 8)}.jpg`;

  // iter v28 r459 — User M1377: scaricare anche il PRIMA (foto originale).
  // iter v28 r472 — User M1382: «Salva in galleria su mobile non fa nulla».
  // Root cause: <a href={remoteUrl} download> è IGNORATO da iOS Safari per
  // URL cross-origin (il backend è su sottodominio diverso). Inoltre, su
  // iOS l'unico modo per finire DAVVERO in Galleria Foto è passare dal
  // share sheet nativo. Strategia:
  //   • mobile (iOS/Android): fetch blob → Web Share API → "Salva immagine"
  //   • desktop: fetch blob → anchor download diretto
  const handleSaveOriginal = async (e) => {
    e?.stopPropagation();
    if (!beforeSrc) return;
    if (isLikelyMobile()) {
      await shareImage({ url: beforeSrc, filename: beforeFilename, title: "", text: "" });
    } else {
      await downloadImage({ url: beforeSrc, filename: beforeFilename });
    }
  };

  // iter v28 r472 — Stessa logica per il bottone "Salva" principale.
  const handleSave = async (e) => {
    e?.stopPropagation();
    if (!curSrc) return;
    if (isLikelyMobile()) {
      await shareImage({ url: curSrc, filename, title: "", text: "" });
    } else {
      await downloadImage({ url: curSrc, filename });
    }
  };

  // ── Action helpers ────────────────────────────────────────────────
  // iter v28 r358 — Niente più testo "Foto curata con Pronto" negli share.
  // iter v30 r095 — User M1750: aggiungiamo un'etichetta neutra del FILE
  // («Ecco la foto/card/video prima e dopo») così il destinatario su WhatsApp
  // capisce cosa sta ricevendo. Niente claim commerciali, solo descrizione.
  const handleShareFoto = (e) => {
    e?.stopPropagation();
    shareImage({ url: curSrc, filename, title: "", text: "Ecco la foto" });
  };
  const handleShareCard = async (e) => {
    e?.stopPropagation();
    // iter v29 r214 — User M1504: «mi dà sempre errore». Aggiungo loader +
    // toast errore esplicito con messaggio chiaro. La card si genera lato
    // server con PIL (~3-6s). Diamo feedback chiaro.
    // iter v29 r221 — User M1547: distingui cancel vs errore vero. Se
    // l'utente annulla lo share sheet, niente toast errore.
    toast.loading("Preparo la card prima/dopo…", { id: "gal-card" });
    try {
      const res = await shareImage({
        url: liveBeforeAfterCardUrl(liveUuid),
        filename: `pronto-prima-dopo-${liveUuid.slice(0, 8)}.png`,
        title: "",
        text: "Ecco la card prima e dopo",
      });
      toast.dismiss("gal-card");
      if (res?.cancelled) return;
      if (!res?.shared && !res?.downloaded) {
        toast.error("Card non generata. Aspetta qualche secondo e riprova.", { id: "gal-card" });
      }
    } catch {
      toast.error("Card non generata. Riprova fra qualche secondo.", { id: "gal-card" });
    }
  };
  const handleShareVideo = async (e) => {
    e?.stopPropagation();
    toast.loading("Preparo il video 6s… (può richiedere fino a 1 minuto)", { id: "gal-video" });
    try {
      const res = await shareVideo({
        url: liveBeforeAfterVideoUrl(liveUuid, "slider"),
        filename: `pronto-prima-dopo-${liveUuid.slice(0, 8)}.mp4`,
        title: "",
        text: "Ecco il video prima e dopo",
      });
      toast.dismiss("gal-video");
      if (res?.cancelled) return;
      if (!res?.shared && !res?.downloaded) {
        toast.error("Video non generato. Riprova fra qualche secondo.", { id: "gal-video" });
      }
    } catch {
      toast.error("Video non generato, riprova", { id: "gal-video" });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/92 backdrop-blur-sm flex flex-col"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      {/* iter v33 r1 — User M1471 (galleria si "freezava"): la X stava a
          `top-3` SENZA env(safe-area-inset-top), sull'iPhone con notch
          finiva sotto la status bar → l'agente non riusciva a chiuderla.
          Pattern allineato a FotoEditorLightbox: fixed, safe-area-top,
          z-[60] (sopra a tutto), bg-white per contrasto sul black/92. */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="fixed top-[max(env(safe-area-inset-top),12px)] right-3 z-[60] w-11 h-11 rounded-full bg-white text-[#1C1C1A] inline-flex items-center justify-center shadow-lg hover:bg-[#F1F4ED] active:scale-95 transition-all"
        aria-label="Chiudi"
        data-testid="gallery-lightbox-close"
      >
        <X size={20} weight="bold" />
      </button>

      <BeforeAfterImage
        kind={h.kind}
        kindLabel={kindLabel}
        beforeSrc={beforeSrc}
        afterSrc={afterSrc}
        isLive={isLive}
        lightboxView={view}
        setLightboxView={setView}
        onClose={onClose}
        testidPrefix="gallery-lightbox"
      />

      {/* iter v28 r358 — Feedback bar (👍/👎) per live items: identica all'Editor */}
      {isLive && liveUuid && (
        <LiveFeedbackBar
          liveId={liveUuid}
          source={h.source}
          onClose={onClose}
        />
      )}

      {/* Bottom actions · primary row (4 bottoni in BOX NERO) */}
      <div
        className="px-3 pb-[max(env(safe-area-inset-bottom),16px)] pt-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#1C1C1A] rounded-2xl shadow-2xl p-2 max-w-2xl mx-auto">
          <div className="grid grid-cols-4 gap-1.5">
            <button
              onClick={handleSave}
              className="bg-white text-[#1C1C1A] py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-[#F1F4ED] transition-colors leading-tight"
              data-testid="gallery-lightbox-dl"
              title="Salva la foto sul dispositivo"
            >
              <DownloadSimple size={13} weight="bold" className="shrink-0" />
              <span>Salva</span>
            </button>
            <button
              onClick={handleShareFoto}
              className="bg-white text-[#1C1C1A] py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-[#F1F4ED] transition-colors leading-tight"
              data-testid="gallery-lightbox-share"
            >
              <Share size={13} weight="bold" className="shrink-0" />
              <span>Condividi</span>
            </button>
            {onCreaContenuti && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                  onCreaContenuti(h);
                }}
                className="bg-white text-[#1C1C1A] py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-[#F1F4ED] transition-colors leading-tight"
                data-testid="gallery-lightbox-contenuti"
                title="Crea post, PDF stampabile o volantino con questa foto"
              >
                <Sparkle size={13} weight="fill" className="shrink-0" />
                <span>Contenuti</span>
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setMoreOpen(true); }}
              className="bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 transition-colors leading-tight"
              data-testid="gallery-lightbox-more"
              title="Altre azioni · video prima/dopo, card, riprova, elimina"
              aria-label="Più azioni"
            >
              <DotsThreeOutline size={16} weight="bold" className="shrink-0" />
            </button>
          </div>
        </div>
      </div>

      {/* iter v28 r358 — Bottom sheet "Più…" con azioni secondarie:
            - Video Prima/Dopo (solo live)
            - Card Prima/Dopo (solo live)
            - Riprova (apre Editor)
            - Usa in annuncio / Apri annuncio
            - Elimina
          Layout mobile-friendly, in slide-up. */}
      {moreOpen && (
        <div
          className="absolute inset-0 z-[60] bg-black/55 flex items-end sm:items-center justify-center"
          onClick={(e) => { e.stopPropagation(); setMoreOpen(false); }}
          data-testid="gallery-more-sheet"
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-[#EAE7DF] flex items-center justify-between">
              <div className="font-editorial text-lg text-[#1C1C1A]">Altre azioni</div>
              <button
                onClick={() => setMoreOpen(false)}
                className="w-8 h-8 rounded-full bg-[#F1F4ED] hover:bg-[#E5E7E0] inline-flex items-center justify-center transition-colors"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="px-3 py-3 space-y-1.5">
              {/* iter v28 r459 — User M1377: "Salva originale" (PRIMA della
                  modifica). Solo per foto live che hanno il before. */}
              {hasBeforeAfter && beforeSrc && (
                <SheetItem
                  icon={DownloadSimple}
                  title="Salva foto originale"
                  desc="Scarica la foto PRIMA della modifica (.jpg)"
                  onClick={() => { setMoreOpen(false); handleSaveOriginal(); }}
                  testid="gallery-more-dl-original"
                />
              )}
              {hasBeforeAfter && (
                <>
                  <SheetItem
                    icon={GridFour}
                    title="Card Prima e Dopo"
                    desc="Immagine editoriale 1080×1350 per IG/WhatsApp"
                    onClick={() => { setMoreOpen(false); handleShareCard(); }}
                    testid="gallery-more-card"
                  />
                  <SheetItem
                    icon={FilmStrip}
                    title="Video Prima/Dopo · 8 sec"
                    desc="MP4 con transizione automatica, ottimo per reel"
                    onClick={() => { setMoreOpen(false); handleShareVideo(); }}
                    testid="gallery-more-video"
                  />
                </>
              )}
              {onRiprova && (
                <SheetItem
                  icon={ArrowsClockwise}
                  title="Riprova nell'Editor"
                  desc="Apri con altri parametri (stile, stanza, riferimento)"
                  onClick={() => { setMoreOpen(false); onClose(); onRiprova(h); }}
                  testid="gallery-more-riprova"
                />
              )}
              {isLive && properties.length > 0 && (
                <SheetItem
                  icon={House}
                  title="Usa in un annuncio"
                  desc="Importa questa foto in un immobile esistente"
                  onClick={() => { setMoreOpen(false); onClose(); setImportingId(realId); }}
                  testid="gallery-more-use"
                />
              )}
              {!isLive && h.property_id && (
                <a
                  href={`/dashboard/property/${h.property_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-start gap-3 px-4 py-3 rounded-xl hover:bg-[#FAF8F3] transition-colors"
                  data-testid="gallery-more-open-property"
                >
                  <span className="shrink-0 w-9 h-9 rounded-full bg-[#F1F4ED] inline-flex items-center justify-center text-[#7C8B6E]">
                    <House size={16} weight="fill" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13px] text-[#1C1C1A]">Apri annuncio</div>
                    <div className="text-[11.5px] text-[#6B6A65]">Vai alla scheda dell&apos;immobile</div>
                  </div>
                </a>
              )}
              <SheetItem
                icon={Trash}
                title="Elimina foto"
                desc="Sposta nel cestino (recuperabile per 30 giorni)"
                danger
                onClick={() => {
                  setMoreOpen(false);
                  onClose();
                  deleteOne(realId, h.source || (isLive ? "live" : "property"));
                }}
                testid="gallery-more-delete"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SheetItem({ icon: Icon, title, desc, onClick, testid, danger }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 px-4 py-3 rounded-xl hover:bg-[#FAF8F3] transition-colors text-left"
      data-testid={testid}
    >
      <span className={`shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center ${
        danger ? "bg-red-100 text-red-600" : "bg-[#F1F4ED] text-[#7C8B6E]"
      }`}>
        <Icon size={16} weight="fill" />
      </span>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-[13px] ${danger ? "text-red-600" : "text-[#1C1C1A]"}`}>{title}</div>
        <div className="text-[11.5px] text-[#6B6A65] leading-snug">{desc}</div>
      </div>
    </button>
  );
}
